import { useEffect, useState } from 'react';
import { History, Loader2, FileSpreadsheet, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { tradeService } from '../services/trade-service';
import type { CargaDelHistorial, FotoDeTarifa } from '../types/trade.types';

/**
 * El historial de cargas del tarifario.
 *
 * 🔴 **Por qué existe.** Cada Excel que se sube **reemplaza** al anterior: el
 * archivo es una foto completa, así que lo que no viene dejó de existir. Eso está
 * bien para operar, pero significaba que el precio de ayer desaparecía sin dejar
 * rastro — y con el Excel como base del negocio, eso es no poder responder
 * "¿cuánto cobrábamos cuando cotizamos ese embarque?" ni "¿por qué esta ruta
 * subió 300 USD?".
 *
 * Son dos vistas: la lista de cargas, y al entrar en una, las tarifas que esa
 * carga pisó, congeladas como estaban.
 */

const LEG_TYPE_LABEL: Record<string, string> = {
  INTERNATIONAL: 'Marítimo',
  INLAND_DESTINATION: 'Terrestre',
  INLAND_ORIGIN: 'Terrestre origen',
};

function fecha(iso: string | Date): string {
  return new Date(iso).toLocaleString('es-BO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function money(v: number | string, currency = 'USD'): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return '—';
  return `${currency} ${n.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function TarifarioHistorial({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [cargas, setCargas] = useState<CargaDelHistorial[] | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** La carga abierta, o `null` si se está viendo la lista. */
  const [abierta, setAbierta] = useState<{
    carga: CargaDelHistorial;
    fotos: FotoDeTarifa[];
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setCargando(true);
    setError(null);
    tradeService
      .historialTarifario()
      .then(setCargas)
      .catch(() => setError('No se pudo leer el historial.'))
      .finally(() => setCargando(false));
  }, [open]);

  // Al cerrar se vuelve a la lista: reabrir directamente en el detalle de una
  // carga que se miró hace tres días es desorientador.
  useEffect(() => {
    if (!open) setAbierta(null);
  }, [open]);

  async function abrir(carga: CargaDelHistorial) {
    setCargando(true);
    try {
      const detalle = await tradeService.detalleCargaTarifario(carga.id);
      setAbierta({ carga, fotos: detalle.fotos });
    } catch {
      setError('No se pudo abrir esa carga.');
    } finally {
      setCargando(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            {abierta ? abierta.carga.fileName : 'Historial del tarifario'}
          </DialogTitle>
          <DialogDescription>
            {abierta
              ? `Las tarifas que estaban vigentes hasta el ${fecha(abierta.carga.uploadedAt)}, cuando se subió este archivo.`
              : 'Cada vez que se sube un Excel, el anterior se reemplaza. Acá queda lo que había antes.'}
          </DialogDescription>
        </DialogHeader>

        {cargando && (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Cargando…
          </div>
        )}

        {error && !cargando && (
          <p className="rounded-md border border-bloqueo-borde bg-bloqueo-suave px-3 py-2 text-sm text-bloqueo">
            {error}
          </p>
        )}

        {/* ── El detalle de una carga ── */}
        {!cargando && abierta && (
          <div className="overflow-auto">
            <Button variant="ghost" size="sm" onClick={() => setAbierta(null)} className="mb-2">
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Volver al historial
            </Button>

            {abierta.fotos.length === 0 ? (
              /*
                Este es el caso de la PRIMERA carga, y merece su propio texto: un
                "sin resultados" genérico haría pensar que algo falló, cuando en
                realidad no había nada que reemplazar.
              */
              <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                Esta carga no reemplazó ninguna tarifa — fue la primera del tarifario.
              </p>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 font-medium">Ruta</th>
                    <th className="py-2 font-medium">Proveedor</th>
                    <th className="py-2 font-medium">Equipo</th>
                    <th className="py-2 pl-4 text-right font-medium">Precio anterior</th>
                    <th className="py-2 pl-4 font-medium">Vigencia</th>
                  </tr>
                </thead>
                <tbody>
                  {abierta.fotos.map((f) => (
                    <tr key={f.id} className="border-b last:border-0">
                      <td className="py-1.5">
                        <span className="font-medium">{f.origin}</span>
                        <span className="mx-1 text-muted-foreground">→</span>
                        <span className="font-medium">{f.destination}</span>
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          {LEG_TYPE_LABEL[f.legType] ?? f.legType}
                        </span>
                      </td>
                      <td className="py-1.5">
                        {f.providerName}
                        {f.serviceType && (
                          <span className="block text-[10px] text-muted-foreground">
                            {f.serviceType}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 font-mono">{f.containerCode ?? '—'}</td>
                      <td className="py-1.5 pl-4 text-right font-semibold tabular-nums">
                        {money(f.amount, f.currency)}
                      </td>
                      <td className="whitespace-nowrap py-1.5 pl-4 text-muted-foreground">
                        {new Date(f.validFrom).toLocaleDateString('es-BO', {
                          day: '2-digit',
                          month: 'short',
                        })}
                        {f.validUntil
                          ? ` → ${new Date(f.validUntil).toLocaleDateString('es-BO', { day: '2-digit', month: 'short' })}`
                          : ' → sin fin'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── La lista de cargas ── */}
        {!cargando && !abierta && cargas && (
          <div className="overflow-auto">
            {cargas.length === 0 ? (
              <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                Todavía no se subió ningún tarifario. La primera carga va a aparecer acá.
              </p>
            ) : (
              <ul className="divide-y">
                {cargas.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => abrir(c)}
                      className="flex w-full items-center gap-3 px-1 py-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <FileSpreadsheet className="h-4 w-4 shrink-0 text-ok" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{c.fileName}</p>
                        <p className="text-xs text-muted-foreground">
                          {fecha(c.uploadedAt)}
                          {c.uploadedBy ? ` · ${c.uploadedBy}` : ''}
                          {c.legTypes.length > 0 &&
                            ` · ${c.legTypes.map((t) => LEG_TYPE_LABEL[t] ?? t).join(' + ')}`}
                        </p>
                      </div>
                      <div className="shrink-0 text-right text-xs">
                        <p className="font-semibold text-ok">{c.ratesImported} cargadas</p>
                        {/*
                          El número de reemplazadas solo aparece si hubo alguna.
                          Un "0 reemplazadas" en la primera carga se lee como un
                          problema; no decir nada se lee como lo que es.
                        */}
                        {c.ratesReplaced > 0 && (
                          <p className="text-muted-foreground">{c.ratesReplaced} reemplazadas</p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
